import { fetchRetry } from './fetch';

const SHOPIFY_STORE_DOMAIN = (window as any).shopUrl;

interface FeaturedImage {
  aspect_ratio: number;
  alt: string;
  height: number;
  url: string;
  width: number;
}

interface OptionWithValue {
  name: string;
  value: string;
}

interface Discount {
  amount: number;
  title: string;
}

interface DiscountApplication {
  type: string;
  key: string;
  title: string;
  description: string | null;
  value: string;
  created_at: string;
  value_type: string;
  allocation_method: string;
  target_selection: string;
  target_type: string;
  total_allocated_amount: number;
}

interface LineLevelDiscountAllocation {
  amount: number;
  discount_application: DiscountApplication;
}

interface QuantityRule {
  min: number;
  max: number | null;
  increment: number;
}

interface SellingPlanOption {
  name: string;
  position: number;
  value: string;
}

interface SellingPlanPriceAdjustment {
  order_count: number | null;
  position: number;
  value_type: string;
  value: number;
}

interface SellingPlan {
  id: number;
  name: string;
  description: string | null;
  options: SellingPlanOption[];
  recurring_deliveries: boolean;
  fixed_selling_plan: boolean;
  price_adjustments: SellingPlanPriceAdjustment[];
}

interface SellingPlanAllocationPriceAdjustment {
  position: number;
  price: number;
}

interface SellingPlanAllocation {
  price_adjustments: SellingPlanAllocationPriceAdjustment[];
  price: number;
  compare_at_price: number;
  per_delivery_price: number;
  selling_plan: SellingPlan;
}

interface UnitPriceMeasurement {
  measured_type: string;
  quantity_value: string;
  quantity_unit: string;
  reference_value: number;
  reference_unit: string;
}

interface CartItem {
  id: number;
  properties: Record<string, string>;
  quantity: number;
  variant_id: number;
  key: string;
  title: string;
  price: number;
  original_price: number;
  discounted_price: number;
  line_price: number;
  original_line_price: number;
  total_discount: number;
  discounts: Discount[];
  sku: string;
  grams: number;
  vendor: string;
  taxable: boolean;
  product_id: number;
  product_has_only_default_variant: boolean;
  gift_card: boolean;
  final_price: number;
  final_line_price: number;
  url: string;
  featured_image: FeaturedImage;
  image: string;
  handle: string;
  requires_shipping: boolean;
  product_type: string;
  product_title: string;
  product_description: string;
  variant_title: string | null;
  variant_options: string[];
  options_with_values: OptionWithValue[];
  line_level_discount_allocations: LineLevelDiscountAllocation[];
  line_level_total_discount: number;
  quantity_rule: QuantityRule;
  has_components: boolean;
  selling_plan_allocation?: SellingPlanAllocation;
  unit_price?: number;
  unit_price_measurement?: UnitPriceMeasurement;
}

export interface ShopifyCart {
  token: string;
  note: string | null;
  attributes: Record<string, string>;
  original_total_price: number;
  total_price: number;
  total_discount: number;
  total_weight: number;
  item_count: number;
  items: CartItem[];
  requires_shipping: boolean;
  currency: string;
  items_subtotal_price: number;
  cart_level_discount_applications: DiscountApplication[];
}

export const getCart = async (): Promise<ShopifyCart | { error: string }> => {
  const response = await fetchRetry({
    url: `${SHOPIFY_STORE_DOMAIN}/cart.js`,
    options: {
      method: 'GET'
    },
    retries: 5,
    retryDelay: 1000
  });

  if (!response?.ok) {
    const errorText = (await response?.text()) || 'Failed to get cart data';
    return {
      error: errorText
    };
  }

  const data = await response.json();

  return data;
};
